import React, { useMemo, useState } from 'react';
import { Card, Button, Table, Spinner, Alert } from 'react-bootstrap';
import {
  useListEntrepreneurProvisionQueueQuery,
  useRejectEntrepreneurProvisionMutation,
  useGetEntrepreneurClaudeCertUrlMutation,
} from '../../app/api';
import { brand, cardStyle, headStyle, EntPage, runAction, FilterBar, filterInputStyle } from './common';
import ActivationChargeModal from './ActivationChargeModal';

export default function Provisioning() {
  // isError matters as much as data here: a 500 or a 403 would otherwise fall
  // through to "No seats awaiting provisioning", which states as fact that there
  // is no work outstanding. That is how a broken endpoint stayed invisible.
  const {
    data: allQueue = [], isLoading, refetch, isError, error,
  } = useListEntrepreneurProvisionQueueQuery();
  const [rejectProvision] = useRejectEntrepreneurProvisionMutation();
  const [getCertUrl] = useGetEntrepreneurClaudeCertUrlMutation();
  const [activationTarget, setActivationTarget] = useState(null);

  const [search, setSearch] = useState('');
  const [certFilter, setCertFilter] = useState('');
  const [billingFilter, setBillingFilter] = useState('');

  const queue = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allQueue.filter(row => {
      if (certFilter === 'certified' && !row.claude_certified) return false;
      if (certFilter === 'not_certified' && row.claude_certified) return false;
      if (billingFilter === 'ready' && !row.firm_billing_ready) return false;
      if (billingFilter === 'not_ready' && row.firm_billing_ready) return false;
      if (q && !`${row.first_name || ''} ${row.last_name || ''} ${row.email || ''} ${row.firm_name || ''}`.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [allQueue, search, certFilter, billingFilter]);

  const viewCertificate = async (uuid) => {
    try {
      const res = await getCertUrl({ uuid }).unwrap();
      if (res?.url) window.open(res.url, '_blank', 'noopener,noreferrer');
    } catch {
      window.alert('Could not open that certificate.');
    }
  };

  const buttonTitle = (q) => {
    if (!q.claude_certified) return 'Member must complete Claude certification first';
    if (!q.firm_billing_ready) return 'No card on file — you can confirm without a charge';
    return 'Enter Claude’s prorated charge (or confirm with no charge)';
  };

  return (
    <EntPage title="Claude Seat Requests" subtitle="Hub members whose firm asked for a Claude seat, across every firm. Add each in the Anthropic console, then confirm here.">
      <Card style={cardStyle}>
        <Card.Header style={{ ...headStyle, display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span>Seat provisioning queue</span>
          <FilterBar search={search} onSearchChange={setSearch} placeholder="Search member or firm…">
            <select value={certFilter} onChange={(e) => setCertFilter(e.target.value)} aria-label="Cert status" style={filterInputStyle}>
              <option value="">Any cert status</option>
              <option value="certified">Certified</option>
              <option value="not_certified">Not certified</option>
            </select>
            <select value={billingFilter} onChange={(e) => setBillingFilter(e.target.value)} aria-label="Billing readiness" style={filterInputStyle}>
              <option value="">Any billing status</option>
              <option value="ready">Firm billing ready</option>
              <option value="not_ready">No payment method</option>
            </select>
          </FilterBar>
        </Card.Header>
        <Card.Body className="p-0">
          {isLoading ? <div className="text-center p-4"><Spinner size="sm" /></div> : isError ? (
            <Alert variant="danger" className="m-3 py-2" style={{ fontSize: '0.85rem' }}>
              <div style={{ fontWeight: 700 }}>
                <i className="bi bi-exclamation-octagon-fill me-1" />
                Couldn&apos;t load the provisioning queue
              </div>
              <div style={{ marginTop: 4 }}>
                {error?.status === 403
                  ? 'Your account does not have permission to view provisioning.'
                  : `The server returned an error${error?.status ? ` (${error.status})` : ''}. Members may be waiting for Claude access — this is not an empty queue.`}
              </div>
              <Button size="sm" variant="outline-danger" className="mt-2" onClick={refetch}>Try again</Button>
            </Alert>
          ) : queue.length === 0 ? (
            <div style={{ padding: '1.5rem', color: brand.charcoal, fontSize: '0.85rem' }}>{allQueue.length === 0 ? 'No seats awaiting provisioning.' : 'No members match these filters.'}</div>
          ) : (
            <Table responsive className="mb-0" style={{ fontSize: '0.85rem' }}>
              <thead><tr style={{ color: brand.charcoal }}>
                <th style={{ padding: '0.6rem 0.9rem' }}>Member</th>
                <th style={{ padding: '0.6rem 0.9rem' }}>Firm</th>
                <th style={{ padding: '0.6rem 0.9rem' }}>Claude cert</th>
                <th style={{ padding: '0.6rem 0.9rem', textAlign: 'right' }} />
              </tr></thead>
              <tbody>
                {queue.map(q => (
                  <tr key={q.uuid}>
                    <td style={{ padding: '0.5rem 0.9rem', verticalAlign: 'middle' }}>
                      <div style={{ fontWeight: 600, color: brand.deepBlue }}>{[q.first_name, q.last_name].filter(Boolean).join(' ') || q.email}</div>
                      <div style={{ color: brand.charcoal, fontSize: '0.75rem' }}>{q.email}</div>
                    </td>
                    <td style={{ padding: '0.5rem 0.9rem', verticalAlign: 'middle', color: brand.charcoal }}>
                      {q.firm_name || '—'}
                      {q.firm_domains && (
                        <div style={{ color: brand.deepBlue, fontSize: '0.72rem' }} title="Requested domain — add this member to this Claude domain in the Anthropic console">
                          <i className="bi bi-globe me-1" />{q.firm_domains}
                        </div>
                      )}
                      {!q.firm_billing_ready && (
                        <div style={{ color: brand.goldenrod, fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          <i className="bi bi-exclamation-triangle-fill me-1" />No payment method
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '0.5rem 0.9rem', verticalAlign: 'middle' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                        {q.claude_certified ? (
                          <span style={{ background: 'rgba(31,138,84,0.12)', color: '#1a7f4e', borderRadius: 20, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                            <i className="bi bi-patch-check-fill me-1" />Certified
                          </span>
                        ) : (
                          <span style={{ background: 'rgba(185,119,14,0.12)', color: '#b9770e', borderRadius: 20, padding: '2px 10px', fontSize: '0.72rem', fontWeight: 700, whiteSpace: 'nowrap' }}>
                            Not yet certified
                          </span>
                        )}
                        {q.has_certificate && (
                          <Button size="sm" variant="outline-secondary" onClick={() => viewCertificate(q.uuid)} title="Open the uploaded certificate" style={{ padding: '0.1rem 0.5rem', fontSize: '0.72rem' }}>
                            <i className="bi bi-file-earmark-check me-1" />View
                          </Button>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '0.5rem 0.9rem', verticalAlign: 'middle', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <Button
                        size="sm" variant="success" className="me-1"
                        disabled={!q.claude_certified}
                        onClick={() => setActivationTarget(q)}
                        title={buttonTitle(q)}
                      >
                        Confirm added to Claude
                      </Button>
                      <Button size="sm" variant="outline-danger" onClick={() => runAction(rejectProvision, { uuid: q.uuid }, refetch)}>Reject</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {activationTarget && (
        <ActivationChargeModal
          member={activationTarget}
          onClose={() => setActivationTarget(null)}
          onCharged={async () => { setActivationTarget(null); await refetch(); }}
        />
      )}
    </EntPage>
  );
}
